import { formView as formViewEvent } from '@wix/bi-logger-forms-platform-uou/v2';
import { FieldActionsContext, FieldValidationContext, Form, FormStatusProvider, getSortedFields, useBi, useFocusedField, } from '@wix/form-fields';
import { deepEqual } from 'fast-equals';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, } from 'react';
import { DATA_HOOKS } from '../../tpa/widget/data-hooks';
import { clearFieldsWhenHiddenByRule } from './clear-fields';
import { fillEmptyValues } from './form-values';
import { getStepIndex } from './get-step-index';
import { useFormValues } from './use-form-values';
import { useFormView } from './use-form-view';
import { useReportFieldEdit } from './use-report-field-edit';
import { useScrollToFirstError } from './use-scroll-to-first-error';
import { useSubmit } from './use-submit';
import { reportErrorChanges, useValidation } from './use-validation';
import { applyAllOverrides } from './all-overrides';
import { SubmitStatusMessage } from '../submit-status-message/submit-status-message';
import { StepTitleA11y } from './step-title-a11y';
export const FormRoot = ({ form: unprocessedForm, // renaming form to prevent accidental use
values: unprocessedValues, // renaming values to prevent accidental use
stepId, onStepIdChange, onChange, errors, onValidate, onSubmit, onSubmitSuccess, onSubmitFailure, forwardedRef, readOnly, overrides, actions, inProgress, config, breakpoint, onStatusChange, validationMode, }) => {
    // `values` – we are working with the full list for all steps.
    // `errors` – working only with the errors list for the fields inside current step.
    const formWithOverrides = useMemo(() => applyAllOverrides(unprocessedForm, unprocessedValues, overrides), [unprocessedForm, unprocessedValues, overrides]);
    const formId = formWithOverrides.id;
    const { setFocusedFieldId } = useFocusedField();
    const normalizedValues = useFormValues(formWithOverrides, unprocessedValues, onChange, config);
    const valuesRef = useRef(normalizedValues);
    valuesRef.current = normalizedValues;
    const errorsRef = useRef(errors);
    errorsRef.current = errors;
    const bi = useBi();
    const reportFieldEdit = useReportFieldEdit(formWithOverrides);
    const onFieldChange = (target, newValue) => {
        const oldValues = valuesRef.current;
        const oldErrors = errorsRef.current;
        if (!deepEqual(newValue, oldValues[target])) {
            reportFieldEdit({
                [target]: newValue,
            });
            const newValues = {
                ...oldValues,
                [target]: newValue,
            };
            const cleared = clearFieldsWhenHiddenByRule(unprocessedForm, overrides, oldValues, newValues, oldErrors);
            onChange(cleared.values);
            onValidate(cleared.errors);
        }
    };
    const _onValidate = (newErrors) => {
        reportErrorChanges({
            bi,
            existingErrors: errors,
            newErrors,
            form: formWithOverrides,
        });
        onValidate(newErrors);
    };
    useEffect(() => {
        bi.report(formViewEvent({ mode: readOnly ? 'preview' : 'live_site' }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const formViews = useFormView(formWithOverrides, stepId, breakpoint);
    const currentView = formViews[0];
    const { validateFields, validateStep, handleFieldValidation } = useValidation({
        form: formWithOverrides,
        errorsRef,
        setErrors: _onValidate,
        validationMode,
    });
    const handleStepChange = useCallback((step) => {
        const newStepId = formWithOverrides.steps?.[step]?.id;
        if (newStepId) {
            const firstFieldInStep = getSortedFields(formWithOverrides, {
                stepId: newStepId,
            })[0];
            onStepIdChange?.(newStepId);
            setFocusedFieldId(firstFieldInStep?.id);
        }
    }, [formWithOverrides, onStepIdChange, setFocusedFieldId]);
    const { scrollToFirstError, handleScrollToFirstError } = useScrollToFirstError({
        form: formWithOverrides,
        handleStepChange,
        currentStepId: stepId,
    });
    const resetFormValues = useCallback(() => {
        onChange(fillEmptyValues(formWithOverrides, {}));
    }, [formWithOverrides, onChange]);
    const validateAndScrollToError = useCallback(async (values) => {
        const validationErrors = await validateFields(values);
        if (validationErrors && validationErrors.length) {
            handleScrollToFirstError(validationErrors);
        }
        return validationErrors;
    }, [validateFields, handleScrollToFirstError]);
    const submitAction = useCallback(async (values) => {
        const response = await actions?.submitForm?.(formId, values);
        return response?.submission;
    }, [actions, formId]);
    const { submitForm } = useSubmit({
        submitAction,
        onSubmit,
        onSubmitSuccess,
        onSubmitFailure,
        resetFormValues,
        validateAndScrollToError,
        values: normalizedValues,
        submitSettings: formWithOverrides.submitSettings,
        navigateToCheckout: actions?.navigateToCheckout,
        handleStepChange,
        onValidate: _onValidate,
        handleScrollToFirstError,
    });
    useImperativeHandle(forwardedRef, () => ({
        submit: submitForm,
        validate: async () => {
            const validationErrors = await validateFields(normalizedValues);
            if (validationErrors && validationErrors.length > 0) {
                scrollToFirstError(validationErrors);
                return false;
            }
            return true;
        },
        validateStep: (step) => validateStep(step, normalizedValues),
    }), [
        submitForm,
        validateStep,
        validateFields,
        normalizedValues,
        scrollToFirstError,
    ]);
    const currentStep = getStepIndex(stepId, formWithOverrides.steps);
    const setNextStep = useCallback(() => handleStepChange(currentStep + 1), [currentStep, handleStepChange]);
    const setPreviousStep = useCallback(() => handleStepChange(currentStep - 1), [currentStep, handleStepChange]);
    const fieldActions = useMemo(() => actions
        ? {
            actions: { ...actions, submitForm, setNextStep, setPreviousStep },
            formId,
            inProgress,
        }
        : { actions: { submitForm, setNextStep, setPreviousStep } }, [actions, formId, inProgress, submitForm, setNextStep, setPreviousStep]);
    if (!formViews?.length || !currentView) {
        return null;
    }
    return (React.createElement(FieldValidationContext.Provider, { value: {
            validateFields: handleFieldValidation,
        } },
        React.createElement(FieldActionsContext.Provider, { value: fieldActions },
            React.createElement(FormStatusProvider, { onStatusChange: onStatusChange },
                React.createElement("div", { "data-hook": DATA_HOOKS.FORM_ROOT },
                    React.createElement(Form, { form: currentView, values: normalizedValues, onFieldChange: onFieldChange, errors: errors, dataHook: DATA_HOOKS.FORM_ROOT }),
                    React.createElement(StepTitleA11y, { total: formWithOverrides.steps?.length, index: currentStep }),
                    React.createElement(SubmitStatusMessage, null))))));
};
//# sourceMappingURL=form-root.js.map